import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { host } from '../../../api/ApiCalls';
import Lottie from 'lottie-react';
import Loading from '../../../assets/Loading.json';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/authStore';
import { Search } from 'lucide-react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.vfs;

const StudentDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [courses, setCourses] = useState([]);
  const { user } = useAuthStore(); // Assuming you have a store for auth
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [downloadOption, setDownloadOption] = useState('');


  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(`${host}/api/student/dashboard`, {
          withCredentials: true,
        });
        console.log('Student Dashboard Data:', res.data);
        setDashboardData(res.data);
      } catch (err) {
        console.error('Error loading student dashboard', err);
      }
    };
    fetchDashboard();
  }, []);

  useEffect(() => {
    const fetchLimitedCourses = async () => {
      try {
        const interestQuery = user?.interests?.length
          ? `?interests=${user.interests.join(',')}`
          : '';
        const response = await axios.get(
          `${host}/api/courses/limited${interestQuery}`,
          {
            withCredentials: true,
          }
        );
        const filteredCourses = response.data.courses.filter(
          (course) => !course.students?.some((s) => s.student === user.id)
        );
        setCourses(filteredCourses);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };

    fetchLimitedCourses();
  }, []);

  const calculateCountdown = (date, startTime) => {
    const now = new Date();
    const sessionDateTime = new Date(`${date}T${startTime}`);
    const diff = sessionDateTime - now;

    if (diff <= 0) return 'Starting Soon or Passed';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    return `${hours}h ${minutes}m remaining`;
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const getFilteredDate = (filter) => {
    const now = new Date();
    switch (filter) {
      case 'lastWeek':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case 'lastMonth':
        const lastMonth = new Date(now);
        lastMonth.setMonth(now.getMonth() - 1);
        return lastMonth;
      case 'last3Months':
        const last3Months = new Date(now);
        last3Months.setMonth(now.getMonth() - 3);
        return last3Months;
      case 'last6Months':
        const last6Months = new Date(now);
        last6Months.setMonth(now.getMonth() - 6);
        return last6Months;
      default:
        return null;
    }
  };

  // Fixed: Use dashboardData.transactions instead of undefined transactions
  const filteredTransactions =
    dashboardData?.transactions?.filter((transaction) => {
      // Search filter - by student name, transaction ID, or course name
      const matchesSearch =
        transaction.student
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        transaction.studentUsername
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        transaction.studentEmail
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        transaction._id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transaction.courseName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());

      // If no date filter is applied, return search matches
      if (!dateFilter) {
        return matchesSearch;
      }

      // Apply date filter if provided - filter by transaction date
      const filterDate = getFilteredDate(dateFilter);
      if (!filterDate) {
        return matchesSearch;
      }

      const transactionDate = new Date(transaction.date);

      // Handle invalid dates
      if (isNaN(transactionDate.getTime())) {
        return matchesSearch;
      }

      return matchesSearch && transactionDate >= filterDate;
    }) || [];

  const handleCSVDownload = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      alert('No transaction data to export.');
      return;
    }

    // Define headers for better CSV structure
    const headers = [
      'Date',
      'Time',
      'Transaction ID',
      'Student Name',
      'Student Username',
      'Student Email',
      'Course Name',
      'Amount',
    ];

    const csvRows = [
      headers.join(','), // Header row
      ...filteredTransactions.map((transaction) => {
        const dateObj = new Date(transaction.date);
        const date = dateObj.toLocaleDateString('en-US', {
          dateStyle: 'medium',
        });
        const time = dateObj.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });

        const row = [
          date || 'N/A',
          time || 'N/A',
          transaction._id || 'N/A',
          transaction.student || 'N/A',
          transaction.studentUsername || 'N/A',
          transaction.studentEmail || 'N/A',
          transaction.courseName || 'N/A',
          transaction.amount || '0',
        ];

        return row
          .map((field) => {
            const value = String(field).replace(/"/g, '""'); // Escape quotes
            return `"${value}"`;
          })
          .join(',');
      }),
    ];

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `teacher_transactions_${
      new Date().toISOString().split('T')[0]
    }.csv`;
    a.click();

    URL.revokeObjectURL(url); // Clean up
  };

  const downloadTransactionsAsPDF = (transactionsData) => {
    if (!transactionsData || transactionsData.length === 0) {
      alert('No transaction data to export.');
      return;
    }

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [25, 35, 25, 40],

      header: {
        margin: [25, 15, 25, 0],
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              {
                stack: [
                  { text: 'AcademiaHub', style: 'headerCompany' },
                  {
                    text: 'Educational Management System',
                    style: 'headerSubtitle',
                  },
                ],
                margin: [0, 5, 0, 0],
              },
              {
                stack: [
                  { text: 'TRANSACTION REPORT', style: 'headerTitle' },
                  {
                    text: `Generated: ${new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`,
                    style: 'headerDate',
                  },
                ],
                alignment: 'right',
                margin: [0, 5, 0, 0],
              },
            ],
          ],
        },
        layout: 'noBorders',
      },

      content: [
        {
          margin: [0, 20, 0, 15],
          table: {
            widths: ['*', '*', '*', '*'],
            body: [
              [
                { text: 'Total Transactions', style: 'summaryLabel' },
                { text: 'Total Amount', style: 'summaryLabel' },
                { text: 'Unique Students', style: 'summaryLabel' },
                { text: 'Unique Courses', style: 'summaryLabel' },
              ],
              [
                {
                  text: transactionsData.length.toString(),
                  style: 'summaryValue',
                },
                {
                  text: `$${transactionsData
                    .reduce((acc, tx) => acc + (parseFloat(tx.amount) || 0), 0)
                    .toFixed(2)}`,
                  style: 'summaryValue',
                },
                {
                  text: new Set(
                    transactionsData.map((tx) => tx.student)
                  ).size.toString(),
                  style: 'summaryValue',
                },
                {
                  text: new Set(
                    transactionsData.map((tx) => tx.courseName)
                  ).size.toString(),
                  style: 'summaryValue',
                },
              ],
            ],
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? '#34495e' : '#ecf0f1'),
            textColor: (rowIndex) => (rowIndex === 0 ? 'white' : '#2c3e50'),
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
          },
        },

        {
          table: {
            headerRows: 1,
            widths: [30, 70, 60, 110, 100, 100, '*', 60],
            body: [
              [
                { text: '#', style: 'tableHeader' },
                { text: 'Date', style: 'tableHeader' },
                { text: 'Time', style: 'tableHeader' },
                { text: 'Transaction ID', style: 'tableHeader' },
                { text: 'Student', style: 'tableHeader' },
                { text: 'Course', style: 'tableHeader' },
                { text: 'Amount', style: 'tableHeader' },
              ],
              ...transactionsData.map((tx, index) => {
                const dateObj = new Date(tx.date);
                const date = dateObj.toLocaleDateString('en-US', {
                  dateStyle: 'medium',
                });
                const time = dateObj.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });

                return [
                  {
                    text: String(index + 1).padStart(3, '0'),
                    style: 'cellData',
                    alignment: 'center',
                    fillColor: '#f8f9fa',
                  },
                  {
                    text: date || 'N/A',
                    style: 'dateText',
                    alignment: 'center',
                  },
                  {
                    text: time || 'N/A',
                    style: 'timeText',
                    alignment: 'center',
                  },
                  { text: tx._id || 'N/A', style: 'cellData' },
                  {
                    stack: [
                      { text: tx.student || 'N/A', style: 'cellData' },
                      { text: tx.studentUsername || '', style: 'timeText' },
                      { text: tx.studentEmail || '', style: 'teacherEmail' },
                    ],
                  },
                  { text: tx.courseName || 'N/A', style: 'cellData' },
                  {
                    text: `$${(parseFloat(tx.amount) || 0).toFixed(2)}`,
                    style: 'statsText',
                    alignment: 'right',
                  },
                ];
              }),
            ],
          },
          layout: {
            fillColor: (rowIndex) =>
              rowIndex === 0
                ? '#2c3e50'
                : rowIndex % 2 === 0
                ? '#fdfdfd'
                : null,
            textColor: (rowIndex) => (rowIndex === 0 ? 'white' : '#2c3e50'),
            hLineColor: '#bdc3c7',
            vLineColor: '#bdc3c7',
            hLineWidth: (i, node) =>
              i === 0 || i === node.table.body.length ? 1 : 0.5,
            vLineWidth: () => 0.5,
            paddingLeft: () => 4,
            paddingRight: () => 4,
            paddingTop: () => 4,
            paddingBottom: () => 4,
          },
        },
      ],

      footer: (currentPage, pageCount) => ({
        margin: [25, 10, 25, 0],
        table: {
          widths: ['*', 'auto', '*'],
          body: [
            [
              {
                text: 'AcademiaHub Educational Management System',
                style: 'footerLeft',
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerCenter',
              },
              { text: '© 2025 - Confidential Document', style: 'footerRight' },
            ],
          ],
        },
        layout: 'noBorders',
      }),

      styles: {
        headerCompany: { fontSize: 18, bold: true, color: '#2c3e50' },
        headerSubtitle: { fontSize: 9, color: '#7f8c8d', italics: true },
        headerTitle: { fontSize: 16, bold: true, color: '#2c3e50' },
        headerDate: { fontSize: 8, color: '#7f8c8d' },

        summaryLabel: { fontSize: 9, bold: true, alignment: 'center' },
        summaryValue: { fontSize: 14, bold: true, alignment: 'center' },

        tableHeader: {
          fontSize: 9,
          bold: true,
          alignment: 'center',
          color: 'white',
        },
        cellData: { fontSize: 8, color: '#2c3e50' },
        dateText: { fontSize: 8, color: '#2c3e50', bold: true },
        timeText: { fontSize: 7, color: '#95a5a6' },
        teacherEmail: { fontSize: 7, color: '#7f8c8d', italics: true },
        statsText: { fontSize: 8, bold: true, color: '#27ae60' },

        footerLeft: { fontSize: 8, color: '#7f8c8d', alignment: 'left' },
        footerCenter: {
          fontSize: 8,
          color: '#2c3e50',
          alignment: 'center',
          bold: true,
        },
        footerRight: { fontSize: 8, color: '#7f8c8d', alignment: 'right' },
      },
    };

    pdfMake
      .createPdf(docDefinition)
      .download(
        `AcademiaHub_Transaction_Report_${
          new Date().toISOString().split('T')[0]
        }.pdf`
      );
  };

  const handleDownloadChange = (e) => {
    const value = e.target.value;
    setDownloadOption(''); // Reset dropdown after selection

    if (value === 'csv') {
      handleCSVDownload();
    } else if (value === 'pdf') {
      downloadTransactionsAsPDF(filteredTransactions);
    }
  };

  return (
    <div
      className={`min-h-screen p-6${dashboardData ? 'bg-gray-50' : 'bg-white'}`}
    >
      <h1 className="text-3xl font-bold mb-6">Student Dashboard</h1>

      {dashboardData ? (
        <>
          {/* 📘 Enrolled Courses */}
          <h2 className="text-2xl font-semibold mb-3">Your Enrolled Courses</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {dashboardData.enrolledCourses.length > 0 ? (
              dashboardData.enrolledCourses.map((course) => (
                <div
                  key={course._id}
                  className="bg-white rounded-lg shadow p-4 hover:shadow-md transition"
                  onClick={() => navigate(`/courses/${course._id}/${user._id}`)} // Assuming you have a route to view course details
                >
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-40 object-cover rounded mb-3"
                  />
                  <h3 className="text-lg font-bold">{course.title}</h3>
                  <p className="text-sm text-gray-600">
                    <strong>Teacher:</strong> {course.teacher.name}
                  </p>
                  <p className="text-sm">
                    <strong>Total Sessions:</strong> {course.totalSessions}
                  </p>
                  <p className="text-sm">
                    <strong>Your Slots:</strong>{' '}
                    {course.selectedSlots?.length
                      ? course.selectedSlots
                          .map(
                            (slot) =>
                              `${slot.date} (${slot.startTime} - ${slot.endTime})`
                          )
                          .join(', ')
                      : 'Not selected'}
                  </p>
                  <p className="text-sm mt-1">
                    <strong>Next Class:</strong>{' '}
                    {course.nextSession
                      ? `${course.nextSession.date} | ${course.nextSession.startTime} - ${course.nextSession.endTime}`
                      : 'No upcoming session'}
                  </p>
                  {course.nextSession && (
                    <p className="text-sm text-blue-600 font-semibold mt-1">
                      {calculateCountdown(
                        course.nextSession.date,
                        course.nextSession.startTime
                      )}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-600">No courses found.</p>
            )}
          </div>
          {/* 🔥 Trending or Latest Courses (Top 6) */}
          <div className="mb-10">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-2xl font-semibold">Recommended Courses</h2>
              <button
                onClick={() => navigate('/courses')}
                className="text-indigo-600 hover:underline text-sm font-medium"
              >
                View All
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses?.length > 0 ? (
                courses.map((course) => (
                  <div
                    key={course._id}
                    className="bg-white p-4 rounded shadow hover:shadow-md transition cursor-pointer"
                    onClick={() => navigate(`/courses/${course._id}`)}
                  >
                    <img
                      src={course.image}
                      alt={course.title}
                      className="w-full h-40 object-cover rounded mb-3"
                    />
                    <h3 className="text-lg font-bold">{course.title}</h3>
                    <p className="text-sm text-gray-600">
                      <strong>Teacher:</strong> {course.teacher.name}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No courses to show.</p>
              )}
            </div>
          </div>
          <div className="mt-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <h2 className="text-2xl font-semibold mb-3 md:mb-0">
                Transaction History
              </h2>
              <div>
                <select
                  onChange={handleDownloadChange}
                  value={downloadOption}
                  className="w-full md:w-auto bg-indigo-500 text-white font-bold py-2 px-4 rounded cursor-pointer"
                >
                  <option
                    value=""
                    disabled
                    className="text-black bg-white border-b"
                  >
                    Select download format
                  </option>
                  <option value="csv" className="text-black bg-white">
                    Download as CSV
                  </option>
                  <option value="pdf" className="text-black bg-white">
                    Download as PDF
                  </option>
                </select>
              </div>
            </div>
          </div>
          {/* Search and Filters section */}
          <div className="flex flex-col md:flex-row gap-4 mb-4 mt-4">
            <div className="flex w-full items-center justify-between gap-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by course, or transaction ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date Filter */}
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Time</option>
                <option value="lastWeek">Last Week</option>
                <option value="lastMonth">Last Month</option>
                <option value="last3Months">Last 3 Months</option>
                <option value="last6Months">Last 6 Months</option>
              </select>
            </div>
          </div>
          {/* Show filtered results count */}
          {(searchQuery || dateFilter) && (
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredTransactions.length} of{' '}
              {dashboardData?.transactions?.length || 0} transactions
            </div>
          )}
          {filteredTransactions.length > 0 ? (
            <div className="overflow-auto rounded-lg border border-gray-200 shadow">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      #
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Transaction Id
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Teacher
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Course
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction, index) => {
                    const dateObj = new Date(transaction.date);
                    const date = dateObj.toLocaleDateString('en-US', {
                      dateStyle: 'medium',
                    });
                    const time = dateObj.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    });
                    return (
                      <tr
                        key={transaction._id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {date}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {time}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {transaction._id}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <div className="font-medium">
                            {transaction.teacher}
                          </div>
                          <div className="text-sm text-gray-500">
                            {transaction.teacherUsername}
                          </div>
                          <div className="text-sm text-gray-400">
                            {transaction.teacherEmail}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {transaction.courseName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          ${transaction.amount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600">No transactions found.</p>
          )}
        </>
      ) : (
        <div className="flex items-center justify-center h-screen">
          <div className="w-80">
            <Lottie animationData={Loading} loop={true} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
